/**
 * Helper function to get category for a player based on ELO
 * @param {Object} playerCategories - Categories object from categorizePlayersForSession
 * @param {string} playerId - Player ID
 * @param {string} gameType - 'mixed' or 'levels'
 * @param {string} gender - Player gender (for levels games)
 * @returns {string|null} Category letter (A, B, C, D) or null
 */
function getPlayerCategory(playerCategories, playerId, gameType, gender) {
  if (!playerCategories) return null;
  
  if (gameType === 'mixed') {
    return playerCategories.mixed?.categories?.[playerId] || null;
  } else {
    // For levels, use gender-specific categories
    const genderKey = gender?.toLowerCase() === 'female' ? 'levels_female' : 'levels_male';
    return playerCategories[genderKey]?.categories?.[playerId] || null;
  }
}

/**
 * Transforms simulation game history to the format expected by SessionReviewGamesTable
 * @param {Array} gameHistory - Array of game objects from simulation
 * @param {Object} playersById - Map of player objects by ID
 * @param {Object} playerCategories - Optional categories object from categorizePlayersForSession
 * @returns {Array} Games data in the format expected by fetchSessionReviewGames
 */
export function transformSimulationToGamesData(gameHistory, playersById, playerCategories = null) {
  // Build pre-game interaction state for each game
  const preGameInteractions = [];
  const preGameGamesPlayed = [];
  
  let currentInteractions = {};
  let currentGamesPlayed = {};
  
  gameHistory.forEach((game, index) => {
    // Save PRE-GAME state (before this game was played)
    preGameInteractions[index] = { ...currentInteractions };
    preGameGamesPlayed[index] = { ...currentGamesPlayed };
    
    // Update state AFTER this game
    const allPlayerIds = [...game.team1PlayerIds, ...game.team2PlayerIds];
    const team1Ids = new Set(game.team1PlayerIds);
    const team2Ids = new Set(game.team2PlayerIds);
    
    // Update games played
    allPlayerIds.forEach(playerId => {
      currentGamesPlayed[playerId] = (currentGamesPlayed[playerId] || 0) + 1;
    });
    
    // Update interactions
    for (let i = 0; i < allPlayerIds.length; i++) {
      for (let j = i + 1; j < allPlayerIds.length; j++) {
        const key = [allPlayerIds[i], allPlayerIds[j]].sort().join('-');
        currentInteractions[key] = (currentInteractions[key] || 0) + 1;
      }
    }
  });
  
  return gameHistory.map((game, index) => {
    const gameType = game.isMixed ? 'mixed' : 'levels';
    
    const team1Players = game.team1Players.map(p => {
      const startingElo = game.isMixed 
        ? (p.starting_mixed_elo || p.mixed_elo_rating || 1200)
        : (p.starting_levels_elo || p.levels_elo_rating || 1200);
      const endingElo = game.isMixed 
        ? (p.mixed_elo_rating || 1200)
        : (p.levels_elo_rating || 1200);
      const category = getPlayerCategory(playerCategories, p.id, gameType, p.gender);
      return {
        id: p.id,
        name: `${p.first_name || ''} ${p.last_name || ''}`.trim() || `Player ${p.id}`,
        gender: p.gender || 'unknown',
        pair_type: game.isMixed ? 'mixed' : 'levels',
        team_number: 1,
        starting_elo: startingElo,
        ending_elo: endingElo,
        elo_change: endingElo - startingElo,
        category: category, // Add category
      };
    });

    const team2Players = game.team2Players.map(p => {
      const startingElo = game.isMixed 
        ? (p.starting_mixed_elo || p.mixed_elo_rating || 1200)
        : (p.starting_levels_elo || p.levels_elo_rating || 1200);
      const endingElo = game.isMixed 
        ? (p.mixed_elo_rating || 1200)
        : (p.levels_elo_rating || 1200);
      const category = getPlayerCategory(playerCategories, p.id, gameType, p.gender);
      return {
        id: p.id,
        name: `${p.first_name || ''} ${p.last_name || ''}`.trim() || `Player ${p.id}`,
        gender: p.gender || 'unknown',
        pair_type: game.isMixed ? 'mixed' : 'levels',
        team_number: 2,
        starting_elo: startingElo,
        ending_elo: endingElo,
        elo_change: endingElo - startingElo,
        category: category, // Add category
      };
    });

    // Calculate average starting ELO for each team
    const team1AvgElo = team1Players.length > 0
      ? Math.round(team1Players.reduce((sum, p) => sum + (p.starting_elo || 1200), 0) / team1Players.length)
      : null;
    const team2AvgElo = team2Players.length > 0
      ? Math.round(team2Players.reduce((sum, p) => sum + (p.starting_elo || 1200), 0) / team2Players.length)
      : null;

    // Get game type name
    const gameTypeNames = {
      1: 'Mixed Doubles',
      2: 'Mens Doubles',
      3: 'Womens Doubles',
      4: 'Mens Singles',
      5: 'Womens Singles',
      6: 'Mens vs Mixed Doubles',
      7: 'Open Singles',
      8: 'Womens vs Mixed Doubles',
    };

    return {
      game_number: index + 1,
      game_id: game.id,
      game_created_at: game.createdAt,
      start_time: game.startTime,
      end_time: game.endTime,
      game_type: gameTypeNames[game.gameType] || 'Unknown',
      is_doubles: game.gameType !== 4 && game.gameType !== 5 && game.gameType !== 7,
      pair_type: game.isMixed ? 'mixed' : 'levels',
      team_1_score: game.team1Score,
      team_2_score: game.team2Score,
      winning_team: game.winningTeam === 1 ? 'Team 1' : game.winningTeam === 2 ? 'Team 2' : 'Draw',
      team_1_players: team1Players,
      team_2_players: team2Players,
      team_1_avg_starting_elo: team1AvgElo,
      team_2_avg_starting_elo: team2AvgElo,
      is_altered: false,
      game_create_method: 'matchmaking_simulation',
      // Store pre-game interaction state for accurate rating
      preGameInteractions: preGameInteractions[index],
      preGameGamesPlayed: preGameGamesPlayed[index],
      // Store matchmaking relaxation data
      matchmakingRelaxed: game.matchmakingRelaxed || false,
      matchmakingRelaxationDetails: game.matchmakingRelaxationDetails || [],
    };
  });
}

/**
 * Transforms simulation game history to the format expected by SessionReviewInteractionsTable
 * @param {Array} gameHistory - Array of game objects from simulation
 * @param {Object} playersById - Map of player objects by ID
 * @returns {Array} Interactions data in the format expected by fetchSessionReviewInteractions
 */
export function transformSimulationToInteractionsData(gameHistory, playersById) {
  const interactionsMap = {};

  // Process each game
  gameHistory.forEach(game => {
    const allPlayers = [
      ...game.team1Players.map(p => ({ ...p, team: 1 })),
      ...game.team2Players.map(p => ({ ...p, team: 2 })),
    ];

    // Find all pairs in this game
    for (let i = 0; i < allPlayers.length; i++) {
      for (let j = i + 1; j < allPlayers.length; j++) {
        const p1 = allPlayers[i];
        const p2 = allPlayers[j];

        // Ensure consistent ordering (smaller ID first)
        const player1Id = p1.id < p2.id ? p1.id : p2.id;
        const player2Id = p1.id < p2.id ? p2.id : p1.id;

        const key = `${player1Id}_${player2Id}`;

        if (!interactionsMap[key]) {
          const player1 = p1.id < p2.id ? p1 : p2;
          const player2 = p1.id < p2.id ? p2 : p1;
          interactionsMap[key] = {
            player1_id: player1Id,
            player1_name: `${player1.first_name || ''} ${player1.last_name || ''}`.trim() || `Player ${player1Id}`,
            player1_gender: player1.gender || 'unknown',
            player2_id: player2Id,
            player2_name: `${player2.first_name || ''} ${player2.last_name || ''}`.trim() || `Player ${player2Id}`,
            player2_gender: player2.gender || 'unknown',
            games_played_together: 0,
            games_played_against: 0,
            total_interactions: 0,
            altered_games_count: 0,
          };
        }

        // Determine interaction type
        if (p1.team === p2.team) {
          interactionsMap[key].games_played_together++;
        } else {
          interactionsMap[key].games_played_against++;
        }
        interactionsMap[key].total_interactions++;
      }
    }
  });

  // Convert map to array and sort
  const interactions = Object.values(interactionsMap);
  interactions.sort((a, b) => {
    if (b.total_interactions !== a.total_interactions) {
      return b.total_interactions - a.total_interactions;
    }
    if (b.games_played_together !== a.games_played_together) {
      return b.games_played_together - a.games_played_together;
    }
    return b.games_played_against - a.games_played_against;
  });

  return interactions;
}

/**
 * Transforms simulation game history to the format expected by SessionReviewPlayerSummaryTable
 * @param {Array} gameHistory - Array of game objects from simulation
 * @param {Object} playersById - Map of player objects by ID
 * @param {Object} playerCategories - Optional categories object from categorizePlayersForSession
 * @returns {Array} Summary data in the format expected by fetchPlayerInteractionSummary
 */
export function transformSimulationToSummaryData(gameHistory, playersById, playerCategories = null) {
  const playerStatsMap = {};

  // Initialize player stats
  const allPlayerIds = new Set();
  gameHistory.forEach(game => {
    game.team1PlayerIds.forEach(id => allPlayerIds.add(id));
    game.team2PlayerIds.forEach(id => allPlayerIds.add(id));
  });

  allPlayerIds.forEach(playerId => {
    const player = playersById[playerId];
    if (player) {
      playerStatsMap[playerId] = {
        player_id: playerId,
        player_name: `${player.first_name || ''} ${player.last_name || ''}`.trim() || `Player ${playerId}`,
        player_gender: player.gender || 'unknown',
        total_games: 0,
        played_with: new Set(),
        played_against: new Set(),
        interaction_counts: {},
        altered_games_count: 0,
      };
    }
  });

  // Process each game
  gameHistory.forEach(game => {
    const allPlayers = [
      ...game.team1Players.map(p => ({ ...p, team: 1 })),
      ...game.team2Players.map(p => ({ ...p, team: 2 })),
    ];

    // Update stats for each player
    allPlayers.forEach(p => {
      const stats = playerStatsMap[p.id];
      if (stats) {
        stats.total_games++;
      }
    });

    // Find all pairs
    for (let i = 0; i < allPlayers.length; i++) {
      for (let j = i + 1; j < allPlayers.length; j++) {
        const p1 = allPlayers[i];
        const p2 = allPlayers[j];

        const stats1 = playerStatsMap[p1.id];
        const stats2 = playerStatsMap[p2.id];

        if (!stats1 || !stats2) continue;

        // Track unique players
        if (p1.team === p2.team) {
          stats1.played_with.add(p2.id);
          stats2.played_with.add(p1.id);
        } else {
          stats1.played_against.add(p2.id);
          stats2.played_against.add(p1.id);
        }

        // Track interaction counts
        if (!stats1.interaction_counts[p2.id]) {
          stats1.interaction_counts[p2.id] = 0;
        }
        if (!stats2.interaction_counts[p1.id]) {
          stats2.interaction_counts[p1.id] = 0;
        }
        stats1.interaction_counts[p2.id]++;
        stats2.interaction_counts[p1.id]++;
      }
    }
  });

  // Convert to array and calculate high interaction counts
  const summaries = Object.values(playerStatsMap).map(stats => {
    // Count how many players this player had >=50% interaction with
    let highInteractionCount = 0;
    Object.entries(stats.interaction_counts).forEach(([otherPlayerId, interactionCount]) => {
      const interactionPercentage = (interactionCount / stats.total_games) * 100;
      if (interactionPercentage >= 50) {
        highInteractionCount++;
      }
    });

    // Get categories for this player
    const player = playersById[stats.player_id];
    const mixedCategory = playerCategories?.mixed?.categories?.[stats.player_id] || null;
    const gender = player?.gender?.toLowerCase();
    const levelsCategory = gender === 'female' 
      ? (playerCategories?.levels_female?.categories?.[stats.player_id] || null)
      : (playerCategories?.levels_male?.categories?.[stats.player_id] || null);
    
    // Format category as "AA" (mixed A, levels A) or "AB" (mixed A, levels B), etc.
    let categoryDisplay = '';
    if (mixedCategory && levelsCategory) {
      categoryDisplay = `${mixedCategory}${levelsCategory}`;
    } else if (mixedCategory) {
      categoryDisplay = `M${mixedCategory}`;
    } else if (levelsCategory) {
      categoryDisplay = `L${levelsCategory}`;
    }

    return {
      player_id: stats.player_id,
      player_name: stats.player_name,
      player_gender: stats.player_gender,
      total_games: stats.total_games,
      unique_played_with: stats.played_with.size,
      unique_played_against: stats.played_against.size,
      high_interaction_count: highInteractionCount,
      altered_games_count: stats.altered_games_count,
      category: categoryDisplay, // Add category display
    };
  });

  // Sort by high interaction count (descending)
  summaries.sort((a, b) => {
    if (b.high_interaction_count !== a.high_interaction_count) {
      return b.high_interaction_count - a.high_interaction_count;
    }
    return b.total_games - a.total_games;
  });

  return summaries;
}

